import Switch from '@/components/Switch/Switch';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { getAvatarElementFromPage, getAvatarGroupFromPage } from '@/pages/AdvancedEditor/utils';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import { MixPanelEvent } from '@/utils/types';
import styled from '@emotion/styled';
import { Divider } from '@mui/material';
import { ElementType } from 'polotno/model/group-model';
import { ControlRow } from '../../../common/ControlRow';
import { FillColorControl } from './FillColorControl';

const FillContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const FillControl = () => {
    const mixpanel = useAnalytics();
    const avatarMaskingFillEnabled = useFlag('avatar_masking_border_enabled', false);
    const { store } = useAdvancedEditorContext();
    const avatarElement = getAvatarElementFromPage(store.activePage);

    if (!avatarElement) return null;

    if (!avatarMaskingFillEnabled) return null;

    const fillEnabled = avatarElement.custom?.fillEnabled;

    const handleSwitchChange = (_: any, checked: boolean) => {
        mixpanel?.track('avatar-masking', { event: MixPanelEvent.CLICK, ['avatar-masking-fill']: checked ? 'applied' : 'not-applied' });

        if (checked) {
            handleEnable();
        } else {
            handleDisable();
        }
        avatarElement.set({ custom: { ...avatarElement.custom, fillEnabled: checked } });
    };

    const handleEnable = () => {
        const group = store.activePage?.children.find(
            child => child.type === 'group' && child.children.map((child: ElementType) => child.id).includes(avatarElement.id)
        );
        const borderCircle = group?.children.find((child: ElementType) => child.name === 'avatar-border');
        const borderCircleArray = borderCircle ? [borderCircle.id] : [];

        const { width, height, x, y } = avatarElement;
        const centerX = x + width / 2;
        const centerY = y + height / 2;

        const fillCircle = store.activePage?.addElement({
            name: 'avatar-fill',
            type: 'figure',
            subType: 'circle',
            x: centerX - width / 2,
            y: centerY - height / 2,
            width,
            height,
            fill: '#000000',
            opacity: 1,
            strokeWidth: 0,
        });

        if (group) {
            store.ungroupElements([group.id]);
        }

        // order is important here
        store.groupElements([fillCircle.id, avatarElement.id, ...borderCircleArray]);
    };

    const handleDisable = () => {
        const group = getAvatarGroupFromPage(store.activePage);

        if (group) {
            const elementsToRemove = group.children
                .filter((child: ElementType) => child.name === 'avatar-fill')
                .map((child: ElementType) => child.id);

            store.deleteElements(elementsToRemove);
            store.selectElements([group.id]);
        }
    };

    return (
        <>
            <FillContainer>
                <ControlRow>
                    Fill
                    <Switch checked={fillEnabled} onChange={handleSwitchChange} />
                </ControlRow>

                <FillColorControl />
            </FillContainer>
            <Divider />
        </>
    );
};
